import { useState } from 'react';
import {
  Activity, BarChart3, Check, CheckCircle, ChevronDown, ChevronUp, Copy,
  Eye, EyeOff, KeyRound, Layers, Lock, Shield, ShieldCheck, Zap,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, PageHeader } from '@/components/ui';
import { useAuth } from '@/lib/auth/useAuth';
import { hasAnyRole } from '@/lib/auth/permissions';
import { cn } from '@/lib/utils/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ProductDoc {
  id: string;
  name: string;
  icon: React.ReactNode;
  endpoint: string;
  method: 'POST' | 'GET';
  color: string;
  description: string;
  deduction: number;
  headers: { name: string; value: string; required: boolean }[];
  requestBody: string;
  responseBody: string;
  errorCodes: { code: string; status: number; meaning: string }[];
  curlExample: string;
  balanceNote: string;
}

// ── Data ──────────────────────────────────────────────────────────────────────

const DOCS: ProductDoc[] = [
  {
    id: 'verify',
    name: 'Authmatech Verify',
    icon: <CheckCircle className="h-5 w-5" />,
    endpoint: '/v1/api/verify',
    method: 'POST',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
    description: 'Silent mobile number verification without OTP. Confirms the SIM in the device owns the claimed MSISDN via the operator network.',
    deduction: 1,
    headers: [
      { name: 'X-API-KEY', value: 'Your API key', required: true },
      { name: 'X-CLIENT-ID', value: 'Your client ID', required: true },
      { name: 'Content-Type', value: 'application/json', required: true },
    ],
    requestBody: JSON.stringify({
      mobileNumber: '+9627xxxxxxxx',
      encryptedMobileNumber: '<SDK-encrypted-payload>',
    }, null, 2),
    responseBody: JSON.stringify({
      success: true,
      data: { validNumber: true, mobileNumber: null },
    }, null, 2),
    errorCodes: [
      { code: 'BALANCE_EXPIRED', status: 402, meaning: 'Your balance has expired. Contact Authmatech to top up.' },
      { code: 'INSUFFICIENT_BALANCE', status: 402, meaning: 'No credits remaining.' },
      { code: 'CLIENT_NOT_FOUND', status: 404, meaning: 'Invalid X-CLIENT-ID.' },
      { code: 'INVALID_API_KEY', status: 401, meaning: 'Invalid or missing X-API-KEY.' },
    ],
    curlExample: `curl -X POST https://api.authmatech.com/v1/api/verify \\
  -H "X-API-KEY: <your-api-key>" \\
  -H "X-CLIENT-ID: <your-client-id>" \\
  -H "Content-Type: application/json" \\
  -d '{"mobileNumber":"+9627xxxxxxxx","encryptedMobileNumber":"<payload>"}'`,
    balanceNote: '1 credit per call, regardless of pass or fail. Credits expire on your balance expiry date.',
  },
  {
    id: 'verify-plus',
    name: 'Authmatech Verify+',
    icon: <Zap className="h-5 w-5" />,
    endpoint: '/v1/api/verify/:transactionId',
    method: 'POST',
    color: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
    description: 'Web-flow verification using a transaction ID from the browser-side SDK. The backend retrieves the encrypted MSISDN from the operator using the transaction ID.',
    deduction: 1,
    headers: [
      { name: 'X-API-KEY', value: 'Your API key', required: true },
      { name: 'X-CLIENT-ID', value: 'Your client ID', required: true },
      { name: 'Content-Type', value: 'application/json', required: true },
    ],
    requestBody: JSON.stringify({
      mobileNumber: '+9627xxxxxxxx',
    }, null, 2),
    responseBody: JSON.stringify({
      success: true,
      data: { validNumber: true, mobileNumber: null },
    }, null, 2),
    errorCodes: [
      { code: 'TRANSACTION_NOT_FOUND', status: 404, meaning: 'Transaction ID not found or expired.' },
      { code: 'BALANCE_EXPIRED', status: 402, meaning: 'Balance has expired.' },
    ],
    curlExample: `curl -X POST https://api.authmatech.com/v1/api/verify/<transactionId> \\
  -H "X-API-KEY: <your-api-key>" \\
  -H "X-CLIENT-ID: <your-client-id>" \\
  -H "Content-Type: application/json" \\
  -d '{"mobileNumber":"+9627xxxxxxxx"}'`,
    balanceNote: '1 credit per call. Same billing as Verify.',
  },
  {
    id: 'signal',
    name: 'Authmatech Signal',
    icon: <Activity className="h-5 w-5" />,
    endpoint: '/v1/api/signal',
    method: 'POST',
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
    description: 'Journey intelligence — captures device and network signals during user sessions. Used to enrich risk scoring and behavioral analytics.',
    deduction: 1,
    headers: [
      { name: 'X-API-KEY', value: 'Your API key', required: true },
      { name: 'X-CLIENT-ID', value: 'Your client ID', required: true },
      { name: 'Content-Type', value: 'application/json', required: true },
    ],
    requestBody: JSON.stringify({
      sessionId: 'sess_abc123',
      metadataId: 'meta_xyz',
      deviceSignal: {
        platform: 'ANDROID',
        sdkVersion: '1.0.0',
        appVersion: '2.1.0',
        isRooted: false,
        isEmulator: false,
        vpnDetected: false,
        networkType: 'CELLULAR',
      },
    }, null, 2),
    responseBody: JSON.stringify({
      success: true,
      data: { sessionId: 'sess_abc123', captured: true },
    }, null, 2),
    errorCodes: [
      { code: 'PRODUCT_NOT_ENABLED', status: 403, meaning: 'Signal product not enabled on your account.' },
    ],
    curlExample: `curl -X POST https://api.authmatech.com/v1/api/signal \\
  -H "X-API-KEY: <your-api-key>" \\
  -H "X-CLIENT-ID: <your-client-id>" \\
  -H "Content-Type: application/json" \\
  -d '{"sessionId":"sess_abc123","deviceSignal":{"platform":"ANDROID","isRooted":false}}'`,
    balanceNote: '1 credit per signal captured.',
  },
  {
    id: 'shield',
    name: 'Authmatech Shield',
    icon: <Shield className="h-5 w-5" />,
    endpoint: '/v1/api/shield/assess',
    method: 'POST',
    color: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
    description: 'Fraud and risk assessment. Evaluates SIM swap risk, device integrity, network signals, and behavioral anomalies. Returns a risk score (0–100) and a decision.',
    deduction: 3,
    headers: [
      { name: 'X-API-KEY', value: 'Your API key', required: true },
      { name: 'X-CLIENT-ID', value: 'Your client ID', required: true },
      { name: 'Content-Type', value: 'application/json', required: true },
    ],
    requestBody: JSON.stringify({
      mobileNumber: '+9627xxxxxxxx',
      metadataId: 'meta_xyz',
      sessionId: 'sess_abc123',
      deviceSignal: {
        platform: 'ANDROID',
        sdkVersion: '1.0.0',
        appVersion: '2.1.0',
        isRooted: false,
        isEmulator: false,
        vpnDetected: false,
        networkType: 'CELLULAR',
        appIntegrityStatus: 'PASS',
      },
      networkSignal: {
        ipCountryCode: 'JO',
        operator: 'ZAIN_JO',
      },
    }, null, 2),
    responseBody: JSON.stringify({
      success: true,
      data: {
        transactionId: 'SH-1716000000000-abc123',
        riskScore: 18,
        riskLevel: 'LOW',
        decision: 'ALLOW',
        reasonCodes: [],
        signals: { deviceRooted: false, emulatorDetected: false, vpnDetected: false },
        metadataId: 'meta_xyz',
        sessionId: 'sess_abc123',
      },
    }, null, 2),
    errorCodes: [
      { code: 'PRODUCT_NOT_ENABLED', status: 403, meaning: 'Shield not enabled on your account.' },
      { code: 'INSUFFICIENT_BALANCE', status: 402, meaning: 'Not enough credits.' },
      { code: 'INVALID_MOBILE_NUMBER', status: 400, meaning: 'Mobile number format invalid.' },
    ],
    curlExample: `curl -X POST https://api.authmatech.com/v1/api/shield/assess \\
  -H "X-API-KEY: <your-api-key>" \\
  -H "X-CLIENT-ID: <your-client-id>" \\
  -H "Content-Type: application/json" \\
  -d '{"mobileNumber":"+9627xxxxxxxx","deviceSignal":{"platform":"ANDROID","isRooted":false}}'`,
    balanceNote: '3 credits per assessment. Deducted after successful evaluation, even if decision is BLOCK.',
  },
  {
    id: 'detect',
    name: 'Authmatech Detect',
    icon: <Eye className="h-5 w-5" />,
    endpoint: '/v1/api/detect/analyze',
    method: 'POST',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
    description: 'Anomaly and threat detection. Identifies repeated attempts, velocity abuse, emulator usage, and multi-account behavior patterns.',
    deduction: 2,
    headers: [
      { name: 'X-API-KEY', value: 'Your API key', required: true },
      { name: 'X-CLIENT-ID', value: 'Your client ID', required: true },
      { name: 'Content-Type', value: 'application/json', required: true },
    ],
    requestBody: JSON.stringify({
      mobileNumber: '+9627xxxxxxxx',
      metadataId: 'meta_xyz',
      sessionId: 'sess_abc123',
      deviceSignal: {
        platform: 'IOS',
        isEmulator: false,
        vpnDetected: false,
      },
    }, null, 2),
    responseBody: JSON.stringify({
      success: true,
      data: {
        transactionId: 'DE-1716000000000-def456',
        anomalyScore: 12,
        threatLevel: 'LOW',
        recommendation: 'ALLOW',
        patterns: { repeatedAttempts: false, highVelocity: false, emulatorDetected: false },
        detectedAnomalies: [],
        metadataId: 'meta_xyz',
        sessionId: 'sess_abc123',
      },
    }, null, 2),
    errorCodes: [
      { code: 'PRODUCT_NOT_ENABLED', status: 403, meaning: 'Detect not enabled on your account.' },
      { code: 'INSUFFICIENT_BALANCE', status: 402, meaning: 'Not enough credits.' },
    ],
    curlExample: `curl -X POST https://api.authmatech.com/v1/api/detect/analyze \\
  -H "X-API-KEY: <your-api-key>" \\
  -H "X-CLIENT-ID: <your-client-id>" \\
  -H "Content-Type: application/json" \\
  -d '{"mobileNumber":"+9627xxxxxxxx","deviceSignal":{"platform":"IOS","isEmulator":false}}'`,
    balanceNote: '2 credits per analysis.',
  },
  {
    id: 'trust',
    name: 'Authmatech Trust',
    icon: <BarChart3 className="h-5 w-5" />,
    endpoint: '/v1/api/trust/score',
    method: 'POST',
    color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300',
    description: 'Unified mobile identity trust score (0–100). Aggregates all available signals into a single trust metric with confidence level and plain-language explanation.',
    deduction: 3,
    headers: [
      { name: 'X-API-KEY', value: 'Your API key', required: true },
      { name: 'X-CLIENT-ID', value: 'Your client ID', required: true },
      { name: 'Content-Type', value: 'application/json', required: true },
    ],
    requestBody: JSON.stringify({
      mobileNumber: '+9627xxxxxxxx',
      metadataId: 'meta_xyz',
      sessionId: 'sess_abc123',
      deviceSignal: {
        platform: 'ANDROID',
        isRooted: false,
        isEmulator: false,
        vpnDetected: false,
        appIntegrityStatus: 'PASS',
        networkType: 'CELLULAR',
      },
      networkSignal: {
        ipCountryCode: 'JO',
        operator: 'ORANGE_JO',
      },
    }, null, 2),
    responseBody: JSON.stringify({
      success: true,
      data: {
        transactionId: 'TR-1716000000000-ghi789',
        trustScore: 87,
        riskLevel: 'LOW',
        recommendation: 'ALLOW',
        confidence: 'HIGH',
        explanation: 'Device integrity verified. Network signals consistent with claimed identity. No anomalies detected.',
        reasonCodes: [],
        metadataId: 'meta_xyz',
      },
    }, null, 2),
    errorCodes: [
      { code: 'PRODUCT_NOT_ENABLED', status: 403, meaning: 'Trust not enabled on your account.' },
      { code: 'INSUFFICIENT_BALANCE', status: 402, meaning: 'Not enough credits.' },
      { code: 'INVALID_MOBILE_NUMBER', status: 400, meaning: 'Mobile number format invalid.' },
    ],
    curlExample: `curl -X POST https://api.authmatech.com/v1/api/trust/score \\
  -H "X-API-KEY: <your-api-key>" \\
  -H "X-CLIENT-ID: <your-client-id>" \\
  -H "Content-Type: application/json" \\
  -d '{"mobileNumber":"+9627xxxxxxxx","deviceSignal":{"platform":"ANDROID","isRooted":false}}'`,
    balanceNote: '3 credits per score. Confidence level (HIGH/MEDIUM/LOW) reflects data quality available for the assessment.',
  },
];

// ── Code block ────────────────────────────────────────────────────────────────

function CodeBlock({ code, lang }: { code: string; lang: string }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className="relative rounded-xl border border-border bg-[#0f1117] text-sm">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
        <span className="text-[11px] text-white/40">{lang}</span>
        <button onClick={copy} className="flex items-center gap-1 text-[11px] text-white/50 hover:text-white transition-colors">
          <Copy className="h-3 w-3" />
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto px-4 py-4 text-[12px] leading-relaxed text-[#e2e8f0]">
        <code>{code}</code>
      </pre>
    </div>
  );
}

// ── Product section ───────────────────────────────────────────────────────────

function ProductSection({ doc }: { doc: ProductDoc }) {
  const [open, setOpen] = useState(true);

  return (
    <div id={doc.id} className="rounded-2xl border border-border bg-surface shadow-card dark:shadow-card-dark overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-5 py-4 text-left hover:bg-surface-subtle transition-colors"
      >
        <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl shrink-0', doc.color)}>
          {doc.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-fg">{doc.name}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="rounded bg-surface-subtle px-1.5 py-0.5 font-mono text-[10px] text-fg-muted">{doc.method}</span>
            <span className="font-mono text-xs text-fg-muted">{doc.endpoint}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs text-fg-muted">{doc.deduction} credit{doc.deduction > 1 ? 's' : ''}</span>
          {open ? <ChevronUp className="h-4 w-4 text-fg-muted" /> : <ChevronDown className="h-4 w-4 text-fg-muted" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-border px-5 pb-6 pt-4 space-y-5">
          <p className="text-sm text-fg-muted">{doc.description}</p>

          {/* Headers */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">Request Headers</p>
            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-surface-muted/40">
                    <th className="px-3 py-2 text-left font-semibold text-fg-muted">Header</th>
                    <th className="px-3 py-2 text-left font-semibold text-fg-muted">Value</th>
                    <th className="px-3 py-2 text-left font-semibold text-fg-muted">Required</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.headers.map((h) => (
                    <tr key={h.name} className="border-b border-border last:border-0">
                      <td className="px-3 py-2 font-mono text-fg">{h.name}</td>
                      <td className="px-3 py-2 text-fg-muted">{h.value}</td>
                      <td className="px-3 py-2">
                        {h.required
                          ? <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Required</span>
                          : <span className="text-fg-muted">Optional</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Request + Response */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">Request Body</p>
              <CodeBlock code={doc.requestBody} lang="JSON" />
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">Response Body</p>
              <CodeBlock code={doc.responseBody} lang="JSON" />
            </div>
          </div>

          {/* Error codes */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">Error Codes</p>
            <div className="rounded-xl border border-border overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-surface-muted/40">
                    <th className="px-3 py-2 text-left font-semibold text-fg-muted">Code</th>
                    <th className="px-3 py-2 text-left font-semibold text-fg-muted">HTTP</th>
                    <th className="px-3 py-2 text-left font-semibold text-fg-muted">Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.errorCodes.map((e) => (
                    <tr key={e.code} className="border-b border-border last:border-0">
                      <td className="px-3 py-2 font-mono text-rose-600 dark:text-rose-400">{e.code}</td>
                      <td className="px-3 py-2 text-fg">{e.status}</td>
                      <td className="px-3 py-2 text-fg-muted">{e.meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* cURL */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">cURL Example</p>
            <CodeBlock code={doc.curlExample} lang="bash" />
          </div>

          {/* Balance note */}
          <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
            <Layers className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <p>{doc.balanceNote}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Credentials card (CLIENT_ADMIN only) ─────────────────────────────────────

function CredentialsCard({ clientId }: { clientId: string }) {
  const [keyVisible, setKeyVisible] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  const curlSample = `curl -X POST "https://api.authmatech.com/v1/api/verify" \\
  -H "X-CLIENT-ID: ${clientId}" \\
  -H "X-API-KEY: <your-api-key>" \\
  -H "Content-Type: application/json" \\
  -d '{"mobileNumber":"+9627xxxxxxxx","encryptedMobileNumber":"<payload>"}'`;

  function copyId() {
    navigator.clipboard.writeText(clientId).then(() => {
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    });
  }

  return (
    <Card accent="primary" elevated>
      <CardHeader>
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-50 text-primary-600 dark:bg-primary-500/15 dark:text-primary-400">
            <KeyRound className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <CardTitle>API Credentials</CardTitle>
            <p className="text-xs text-fg-muted mt-0.5">
              Use these values in all server-side API requests.
            </p>
          </div>
        </div>
      </CardHeader>

      {/* Security warning */}
      <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-500/20 dark:bg-amber-500/10">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
        <p className="text-xs text-amber-800 dark:text-amber-300">
          Never expose your API key in frontend code, mobile apps, or public repositories.
          Always call Authmatech APIs from your server side.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {/* Customer ID */}
        <div className="rounded-xl border border-border bg-surface-subtle p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-fg-muted">
            Customer ID <span className="ml-1 text-[9px] font-normal normal-case text-fg-subtle">X-CLIENT-ID header</span>
          </p>
          <p className="mt-2 font-mono text-[13px] font-medium text-fg break-all leading-snug">
            {clientId}
          </p>
          <button
            type="button"
            onClick={copyId}
            className="mt-3 flex items-center gap-1.5 text-[11px] font-medium text-fg-muted transition-colors hover:text-fg"
          >
            {copiedId ? (
              <><Check className="h-3 w-3 text-emerald-500" /> <span className="text-emerald-600 dark:text-emerald-400">Copied</span></>
            ) : (
              <><Copy className="h-3 w-3" /> Copy Customer ID</>
            )}
          </button>
        </div>

        {/* API Key */}
        <div className="rounded-xl border border-border bg-surface-subtle p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-fg-muted">
            API Key <span className="ml-1 text-[9px] font-normal normal-case text-fg-subtle">X-API-KEY header</span>
          </p>
          <p className={cn('mt-2 font-mono text-[13px] text-fg leading-snug', !keyVisible && 'tracking-widest text-fg-muted')}>
            {keyVisible
              ? <span className="text-sm italic text-fg-muted">Issued at account activation — not retrievable here</span>
              : '••••••••••••••••••••••••'}
          </p>
          <button
            type="button"
            onClick={() => setKeyVisible((v) => !v)}
            className="mt-3 flex items-center gap-1.5 text-[11px] font-medium text-fg-muted transition-colors hover:text-fg"
          >
            {keyVisible
              ? <><EyeOff className="h-3 w-3" /> Hide</>
              : <><Eye className="h-3 w-3" /> Reveal info</>}
          </button>
        </div>
      </div>

      {/* cURL example */}
      <div className="mt-4">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-fg-muted">
          Example request
        </p>
        <CodeBlock code={curlSample} lang="bash" />
      </div>

      {/* Lock note */}
      <div className="mt-4 flex items-start gap-2 rounded-xl bg-surface-subtle px-4 py-3">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-subtle" aria-hidden />
        <p className="text-[11px] text-fg-muted">
          Your API key was issued once at account activation and is not stored in retrievable form
          in this portal. If you need a new key, contact your account manager or open a support
          request — key rotation will invalidate the previous key.
        </p>
      </div>
    </Card>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ClientDeveloperDocsPage() {
  const { user } = useAuth();
  const isClientAdmin = hasAnyRole(user?.roles ?? [], ['AUTHMATECH_CLIENT_ADMIN']);
  const clientId = user?.clientId;

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Developer Documentation"
        subtitle="API reference for all Authmatech products enabled on your account."
      />

      {/* Credentials (CLIENT_ADMIN only) */}
      {isClientAdmin && clientId && (
        <CredentialsCard clientId={clientId} />
      )}

      {/* Quick nav */}
      <Card>
        <CardHeader><CardTitle>Products</CardTitle></CardHeader>
        <div className="flex flex-wrap gap-2">
          {DOCS.map((d) => (
            <a
              key={d.id}
              href={`#${d.id}`}
              className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-fg hover:bg-surface-subtle transition-colors"
            >
              <span className={cn('flex h-4 w-4 items-center justify-center rounded', d.color)}>
                {d.icon}
              </span>
              {d.name}
            </a>
          ))}
        </div>
      </Card>

      {/* Auth note */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-4 dark:border-blue-500/20 dark:bg-blue-500/10">
        <p className="mb-1 text-sm font-semibold text-blue-800 dark:text-blue-300">Authentication</p>
        <p className="text-xs text-blue-700 dark:text-blue-400">
          All product APIs require{' '}
          <code className="rounded bg-blue-100 px-1 dark:bg-blue-500/20">X-API-KEY</code> and{' '}
          <code className="rounded bg-blue-100 px-1 dark:bg-blue-500/20">X-CLIENT-ID</code> headers.
          Never expose your API key on the client side.
        </p>
      </div>

      {/* Product docs */}
      <div className="space-y-4">
        {DOCS.map((doc) => <ProductSection key={doc.id} doc={doc} />)}
      </div>

      {/* SDK note */}
      <Card>
        <CardHeader><CardTitle>SDK</CardTitle></CardHeader>
        <p className="text-sm text-fg-muted">
          Native mobile SDKs (Android and iOS) for device signal collection and encrypted MSISDN payloads
          are available under a separate integration guide. Contact your Authmatech account manager to
          request SDK access and integration documentation.
        </p>
      </Card>
    </div>
  );
}
